import { useEffect, useState, useCallback } from 'react';
import dayjs from 'dayjs';
import {
  getPlanningConfig,
  getServiceProviders,
  getEmployeeSchedules,
  getMinEmployeeConfigs,
  getScheduleRules,
  getApprovedTimeOff,
  getSickLeaves,                //  ← NEW
} from '../api/scheduleApi';

/**
 * Loads all scheduling data for one “period”.
 *
 * @param {string} businessId
 * @param {number} periodOffset  0 = current, -1 = previous, 1 = next …
 */
export default function useScheduleData(businessId, periodOffset = 0) {
  const [state, setState] = useState({
    loading     : true,
    error       : null,
    config      : null,
    providers   : [],
    scheduleMap : {},   // { [empId]: { [ISO]: Shift[] } }
    headCount   : {},   // { [ISO]: employees scheduled }
    minConfig   : null,
    rules       : [],
    range       : { start: null, end: null },
    timeOff     : [],   // ⬅︎ time-off + sick leave merged here
  });

  /* ───────────── loader ───────────── */
  const initialise = useCallback(async () => {
    setState(s => ({ ...s, loading: true }));
    try {
      /* static configuration & look-ups */
      const [
        cfg,
        providers,
        minCfg,
        rules,
        approvedTimeOff,
        sickLeaves,
      ] = await Promise.all([
        getPlanningConfig(businessId),
        getServiceProviders(businessId),
        getMinEmployeeConfigs(businessId),
        getScheduleRules(businessId),
        getApprovedTimeOff(businessId),
        getSickLeaves(businessId),      //  ← NEW
      ]);

      /* merge leave types and tag them */
      const leaveEvents = [
        ...approvedTimeOff.map(r => ({ ...r, type: 'TIME_OFF' })),
        ...sickLeaves.map(r   => ({ ...r, type: 'SICK' })),
      ];

      /* period window (always Monday-based) */
      const windowDays = cfg.upload_every_n_days ?? 7;
      const baseStart  = dayjs().startOf('week').add(1, 'day');      // Mon
      const start      = baseStart.add(periodOffset * windowDays, 'day');
      const end        = start.add(windowDays - 1, 'day');

      /* schedules inside that window */
      const raw = await getEmployeeSchedules(
        businessId,
        start.format('YYYY-MM-DD'),
        end.format('YYYY-MM-DD'),
      );

      const scheduleMap = {};
      const headCount   = {};

      raw.forEach(rec => {
        Object.entries(rec.schedule).forEach(([iso, shifts]) => {
          if (!scheduleMap[rec.employee_id]) scheduleMap[rec.employee_id] = {};
          scheduleMap[rec.employee_id][iso] = shifts;

          if (shifts.length) headCount[iso] = (headCount[iso] || 0) + 1;
        });
      });

      setState({
        loading : false,
        error   : null,
        config  : cfg,
        providers,
        minConfig : minCfg,
        rules,
        range   : { start, end },
        scheduleMap,
        headCount,
        timeOff : leaveEvents,    //  ← unified leave array
      });
    } catch (err) {
      console.error(err);
      setState(s => ({ ...s, loading: false, error: 'Failed to load schedule data' }));
    }
  }, [businessId, periodOffset]);

  useEffect(() => { initialise(); }, [initialise]);

  return { ...state, reload: initialise };
}
